import React, { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, AlertTriangle, Workflow, IndianRupee, ArrowUpRight, Search, CheckCircle2, Sparkles } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import { toast } from "sonner";

const MARKETING_IDEAS = [
  { title: "Repeat-order 60-day win-back", body: "Message customers who last ordered 60+ days ago with a 10% loyalty discount." },
  { title: "Festival kits — corporate gifting", body: "Reach out to your top 10 corporate clients with a festive pre-book offer." },
  { title: "Employee onboarding uniform bundle", body: "Pitch onboarding kits to HR contacts — great time as new financial year hiring picks up." },
  { title: "Instagram testimonial reels", body: "DM your top 5 happiest customers for a 20-second selfie review — remix into a reel." },
  { title: "Sample-to-order conversion push", body: "Every sample DC older than 3 days gets a personal WhatsApp today." },
  { title: "Referral drive: ₹500 credit for both", body: "Ask 20 recent customers if they know another team needing custom apparel." },
  { title: "College fest / bulk enquiry sweep", body: "Reach out to campus event societies before the festive & fest season lock-ups." },
  { title: "Instagram carousel: HSN & GST clarity", body: "Educate B2B buyers with a carousel on GST + invoicing — attracts corporate leads." },
];
function todaysIdea() {
  const day = new Date().getDate();
  return MARKETING_IDEAS[day % MARKETING_IDEAS.length];
}

const toneBadge = { info: "badge-info", warn: "badge-warn", danger: "badge-danger", success: "badge-success", neutral: "badge-neutral" };

const SUB_TITLES = {
  quotes_awaiting: "Quotes Awaiting Response",
  quotes_accepted: "Accepted — Ready to Order",
  quotes_declined: "Declined — Negotiate",
  credit_overdue: "Credit Period Overdue",
  samples_overdue: "Samples Overdue 5+ Days",
  advance_pending: "Advance Not Yet Collected",
  cold_leads: "Cold 60+ Days — Send Nudge",
  artwork_awaiting: "Artwork Awaiting Approval",
  artwork_changes: "Artwork Needs Modification",
  ready_to_procure: "Ready to Order Material",
  po_awaiting: "PO Placed — Awaiting Delivery",
  ready_to_customize: "Ready to Send for Customization",
  in_customization: "In Customization",
  ready_for_dispatch: "Ready for QC & Dispatch",
  balance_pending: "Dispatched — Balance Pending",
};

function itemLink(it) {
  if (it.type === "quote" || it.type === "quote_accepted" || it.type === "quote_declined") return "/quotes";
  if (it.type === "balance_pending") return "/invoices";
  if (it.type === "cold_lead") return "/leads";
  if (it.type === "sample_overdue") return "/samples";
  return `/orders/${it.id}`;
}

const SubBucket = ({ groupKey, items, filter, onMarkContacted }) => {
  const shown = filter ? items.filter(x => (x.title||"").toLowerCase().includes(filter.toLowerCase())) : items;
  return (
    <div className="card-surface p-4" data-testid={`bucket-${groupKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-sm">{SUB_TITLES[groupKey] || groupKey}</div>
        <span className="badge badge-neutral mono">{items.length}</span>
      </div>
      <div className="space-y-1 max-h-52 overflow-y-auto">
        {shown.length === 0 && <div className="text-xs text-[#757169] p-1">Nothing here.</div>}
        {shown.slice(0, 20).map((it, i) => {
          const isCold = it.type === "cold_lead";
          const days = String(it.badge || "").replace(/\D/g, "") || "60+";
          const waMsg = isCold
            ? `Hi ${it.title}, this is Marsh Clothing. It's been ~${days} days since we last spoke — how many days has it been since you last placed a custom apparel order? We'd love to help with your next requirement.`
            : `Hi ${it.title}, from Marsh Clothing.`;
          return (
            <div key={it.id + i} className="flex items-center justify-between text-xs p-1.5 rounded hover:bg-[#F4EDDF] group">
              <Link to={itemLink(it)} className="min-w-0 flex-1">
                <div className="font-medium truncate">{it.title}</div>
                <div className="text-[10px] text-[#757169] truncate">{it.sub}</div>
              </Link>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                {it.badge && <span className={`badge ${toneBadge[it.tone]||"badge-neutral"} mono`} style={{fontSize:"10px",padding:"2px 6px"}}>{it.badge}</span>}
                {it.phone && <WhatsAppButton phone={it.phone} message={waMsg} label="" size="sm"/>}
                {isCold && (
                  <button
                    className="text-[#4A7258] hover:bg-[#DDEADE] p-1 rounded"
                    title="Mark contacted — resets 60-day timer"
                    data-testid={`mark-contacted-${it.id}`}
                    onClick={() => onMarkContacted?.(it)}
                  >
                    <CheckCircle2 size={13}/>
                  </button>
                )}
                <Link to={itemLink(it)}><ArrowUpRight size={11} className="text-[#757169]"/></Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Group = ({ title, icon: Icon, tone, data, keys, filter, onMarkContacted }) => {
  const totalCount = keys.reduce((a,k)=>a+(data[k]?.length||0),0);
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${tone}`}><Icon size={14}/></div>
        <h3 className="font-display font-bold text-base">{title}</h3>
        <span className="badge badge-neutral mono ml-1">{totalCount}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {keys.map(k => <SubBucket key={k} groupKey={k} items={data[k]||[]} filter={filter} onMarkContacted={onMarkContacted}/>)}
      </div>
    </section>
  );
};

export default function ActionBoard() {
  const [data, setData] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [filter, setFilter] = useState("");

  const reload = () => {
    api.get("/action-board").then(r => setData(r.data));
    api.get("/festivals").then(r => setFestivals(r.data.filter(f => f.trigger))).catch(()=>{});
  };
  useEffect(() => { reload(); }, []);

  const markContacted = async (it) => {
    try {
      await api.post("/follow-ups", {
        customer_name: it.title,
        lead_id: it.id,
        channel: "whatsapp",
        outcome: "contacted",
        notes: "Marked contacted from Action Board (60-day nudge)",
      });
      toast.success(`Marked ${it.title} as contacted`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="p-8 text-[#757169]">Loading…</div>;

  const kpis = [
    { label: "Sales Pipeline", tone: "text-[#3D5A80]", count: Object.values(data.sales_pipeline).reduce((a,b)=>a+b.length,0) },
    { label: "Needs Attention", tone: "text-[#B83A3A]", count: Object.values(data.needs_attention).reduce((a,b)=>a+b.length,0) },
    { label: "In Production", tone: "text-[#D99036]", count: Object.values(data.production).reduce((a,b)=>a+b.length,0) },
    { label: "Balance Pending", tone: "text-[#4A7258]", count: Object.values(data.financial).reduce((a,b)=>a+b.length,0) },
  ];

  return (
    <div className="space-y-7">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tiny mb-1">Today · Action Board</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">What's next</h1>
          <p className="text-sm text-[#757169] mt-1">Every card is a next step. Tap to open, tap to send.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-[#757169]"/>
            <input className="input-field pl-8 w-64" placeholder="Search cards…" value={filter} onChange={e=>setFilter(e.target.value)} data-testid="board-search"/>
          </div>
          <button className="btn-ghost text-xs" onClick={reload}>Refresh</button>
        </div>
      </div>

      {festivals.length > 0 && (
        <div className="card-surface p-4 border-l-4 border-l-[#D99036]" data-testid="festival-banner">
          <div className="label-tiny mb-1 text-[#7A5117]">Festival window open</div>
          <div className="text-sm">
            {festivals.map(f => (
              <span key={f.name} className="mr-4"><strong className="font-display">{f.name}</strong> in <span className="mono">{f.days_until}</span> days</span>
            ))}
            <span className="text-[#757169]"> · Reach out to bulk customers now.</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map(k => (
          <div key={k.label} className="card-surface p-4">
            <div className="label-tiny">{k.label}</div>
            <div className={`font-display text-3xl font-bold mt-1 ${k.tone}`}>{k.count}</div>
          </div>
        ))}
      </div>

      {/* Idle-time marketing nudge — appears when the team is between fires */}
      {kpis.reduce((a,k) => a + k.count, 0) < 3 && (() => {
        const idea = todaysIdea();
        return (
          <div className="card-surface p-5 border-l-4 border-l-[#4A7258]" data-testid="marketing-nudge">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#DDEADE] flex items-center justify-center shrink-0">
                <Sparkles size={16} className="text-[#2E4A38]"/>
              </div>
              <div className="flex-1">
                <div className="label-tiny mb-1 text-[#2E4A38]">Quiet moment? Try this instead</div>
                <div className="font-display font-semibold text-base">{idea.title}</div>
                <div className="text-sm text-[#75706A] mt-1">{idea.body}</div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Link to="/leads" className="btn-primary text-xs">Open Leads</Link>
                  <Link to="/customers" className="btn-ghost text-xs">Open Customers</Link>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <Group title="Sales Pipeline" icon={TrendingUp} tone="bg-[#E5ECF5] text-[#2C4763]"
        data={data.sales_pipeline}
        keys={["quotes_awaiting","quotes_accepted","quotes_declined"]}
        filter={filter} onMarkContacted={markContacted} />
      <Group title="Needs Attention" icon={AlertTriangle} tone="bg-[#F7DFDF] text-[#7C2626]"
        data={data.needs_attention}
        keys={["credit_overdue","samples_overdue","advance_pending","cold_leads"]}
        filter={filter} onMarkContacted={markContacted} />
      <Group title="Production Pipeline" icon={Workflow} tone="bg-[#FBEDD6] text-[#7A5117]"
        data={data.production}
        keys={["artwork_awaiting","artwork_changes","ready_to_procure","po_awaiting","ready_to_customize","in_customization","ready_for_dispatch"]}
        filter={filter} onMarkContacted={markContacted} />
      <Group title="Financial" icon={IndianRupee} tone="bg-[#DDEADE] text-[#2E4A38]"
        data={data.financial} keys={["balance_pending"]} filter={filter} onMarkContacted={markContacted} />
    </div>
  );
}
